import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Truck, Heart, User, Menu, X, ChevronDown, ChevronRight } from 'lucide-react';
import { useStore } from '../admin/context/ProductStore';
import { useAuth } from '../context/AuthContext';
import LoginModal from './LoginModal';
import { categories, subcategories } from '../data/data';
import './Header.css';

const beforeLinks = [
  { label: 'Home',  href: '/' },
  { label: 'About', href: '/about' },
];

const afterLinks = [
  { label: 'Combos', href: '/shop?cat=snacks' },
];

export default function Header({ cartCount = 0, onOpenCart }) {
  const { settings, getFullUrl } = useStore();
  const [scrolled,      setScrolled]      = useState(false);
  const [mobileOpen,    setMobileOpen]    = useState(false);
  const [searchOpen,    setSearchOpen]    = useState(false);
  const [searchQuery,   setSearchQuery]   = useState('');
  const [shopOpen,      setShopOpen]      = useState(false);
  const [activeMegaCat, setActiveMegaCat] = useState(categories[0].id);
  const [mobileShopOpen, setMobileShopOpen] = useState(false);
  const { user } = useAuth();
  const [isLoginModalOpen, setLoginModalOpen] = useState(false);
  const navigate = useNavigate();
  const shopRef = useRef(null);

  // Dynamic placeholder logic
  const searchSuggestions = [
    'Search for Almonds...',
    'Search for Walnuts...',
    'Search for Cashews...',
    'Search for Dates...',
    'Search for Saffron...',
    'Search for Pistachios...',
    'Search for Makhana...',
  ];
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [placeholderText, setPlaceholderText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const currentFullText = searchSuggestions[placeholderIndex];
    const typingSpeed = isDeleting ? 50 : 100;

    const timeout = setTimeout(() => {
      if (!isDeleting && placeholderText === currentFullText) {
        setTimeout(() => setIsDeleting(true), 1500); // Wait before starting to delete
      } else if (isDeleting && placeholderText === '') {
        setIsDeleting(false);
        setPlaceholderIndex((prev) => (prev + 1) % searchSuggestions.length);
      } else {
        const nextText = isDeleting 
          ? currentFullText.substring(0, placeholderText.length - 1)
          : currentFullText.substring(0, placeholderText.length + 1);
        setPlaceholderText(nextText);
      }
    }, typingSpeed);

    return () => clearTimeout(timeout);
  }, [placeholderText, isDeleting, placeholderIndex]);

  // Close mega menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (shopRef.current && !shopRef.current.contains(e.target)) {
        setShopOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery)}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  const activeSubs = subcategories[activeMegaCat] || [];

  return (
    <>
      {/* Announcement bar */}
      <div className="announcement-bar">
        🌿 Free shipping on orders above ₹599 &nbsp;·&nbsp; 100% Natural &nbsp;·&nbsp; No Preservatives &nbsp;·&nbsp; Lab Tested
      </div>

      <header className={`header ${scrolled ? 'header--scrolled' : ''}`}>
        <div className="header-inner container">

          {/* Logo */}
          <Link to="/" className="logo">
            {settings?.logo ? (
              <img src={getFullUrl(settings.logo)} alt="KOA" className="logo-img" />
            ) : (
              <img src="/koa-logo.png" alt="KOA" className="logo-img" />
            )}
          </Link>

          {/* Desktop Nav */}
          <nav className="nav-desktop">
            {/* Before Categories: Home, About */}
            {beforeLinks.map(link => (
              <div key={link.label} className="nav-item">
                <Link to={link.href} className="nav-link">{link.label}</Link>
              </div>
            ))}

            {/* Categories mega menu */}
            <div
              className={`nav-item ${shopOpen ? 'active' : ''}`}
              ref={shopRef}
              onMouseEnter={() => setShopOpen(true)}
              onMouseLeave={() => setShopOpen(false)}
            >
              <span 
                className="nav-link nav-link-shop"
                onClick={() => setShopOpen(!shopOpen)}
              >
                Categories <ChevronDown size={14} className="nav-chevron" style={{ transform: shopOpen ? 'rotate(180deg)' : 'none' }} />
              </span>

              {shopOpen && (
                <div className="mega-menu">
                  {/* Left: category list */}
                  <div className="mega-cats">
                    <p className="mega-heading">Categories</p>
                    {categories.map(cat => (
                      <div
                        key={cat.id}
                        className={`mega-cat-item ${activeMegaCat === cat.id ? 'active' : ''}`}
                        onMouseEnter={() => setActiveMegaCat(cat.id)}
                      >
                        <span className="mega-cat-emoji">{cat.emoji}</span>
                        <span className="mega-cat-name">{cat.name}</span>
                        <ChevronRight size={13} className="mega-cat-arrow" />
                      </div>
                    ))}
                    <Link to="/shop" className="mega-view-all" onClick={() => setShopOpen(false)}>
                      View All Products →
                    </Link>
                  </div>

                  {/* Right: subcategories */}
                  <div className="mega-subs">
                    <p className="mega-heading">
                      {categories.find(c => c.id === activeMegaCat)?.name}
                    </p>
                    <div className="mega-subs-grid">
                      {activeSubs.map(sub => (
                        <Link
                          key={sub.id}
                          to={`/shop?cat=${activeMegaCat}&sub=${sub.id}`}
                          className="mega-sub-item"
                          onClick={() => setShopOpen(false)}
                        >
                          <span className="mega-sub-dot" />
                          {sub.name}
                        </Link>
                      ))}
                    </div>
                    {/* Category image */}
                    <div className="mega-cat-preview">
                      <img
                        src={categories.find(c => c.id === activeMegaCat)?.image}
                        alt={activeMegaCat}
                        onError={e => e.target.style.display = 'none'}
                      />
                      <div className="mega-preview-overlay">
                        <Link
                          to={`/shop?cat=${activeMegaCat}`}
                          className="mega-preview-btn"
                          onClick={() => setShopOpen(false)}
                        >
                          Shop {categories.find(c => c.id === activeMegaCat)?.name} →
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* After Categories: Combos */}
            {afterLinks.map(link => (
              <div key={link.label} className="nav-item">
                <Link to={link.href} className="nav-link">{link.label}</Link>
              </div>
            ))}
          </nav>

          {/* Integrated Search Bar (Desktop) */}
          <div className="header-search-desktop">
            <form onSubmit={handleSearch} className="header-search-form">
              <Search size={18} className="header-search-icon" />
              <input
                type="text"
                placeholder={placeholderText}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="header-search-input"
              />
            </form>
          </div>

          {/* Actions */}
          <div className="header-actions">
            <button className="action-btn action-btn-search" onClick={() => setSearchOpen(true)} aria-label="Search">
              <Search size={20} />
            </button>
            <Link to="/wishlist" className="action-btn" aria-label="Wishlist">
              <Heart size={20} />
            </Link>
            <button 
              className="action-btn" 
              onClick={() => user ? navigate('/dashboard') : setLoginModalOpen(true)}
              aria-label="Account"
            >
              <User size={20} />
            </button>
            <button className="action-btn cart-btn" onClick={onOpenCart} aria-label="Cart">
              <Truck size={20} />
              {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
            </button>
            <button className="action-btn mobile-menu-btn" onClick={() => setMobileOpen(true)}>
              <Menu size={22} />
            </button>
          </div>
        </div>

        {/* Search overlay */}
        {searchOpen && (
          <div className="search-overlay">
            <form onSubmit={handleSearch} className="search-form container">
              <Search size={20} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
              <input
                type="text"
                placeholder={placeholderText}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                autoFocus
                className="search-input"
              />
              <button type="button" className="search-close" onClick={() => setSearchOpen(false)}>
                <X size={20} />
              </button>
            </form>
          </div>
        )}
      </header>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="mobile-menu">
          <div className="mobile-menu-header">
            {settings?.logo ? (
              <img src={getFullUrl(settings.logo)} alt="KOA" className="logo-img" style={{ height: '80px', width: '80px' }} />
            ) : (
              <img src="/koa-logo.png" alt="KOA" className="logo-img" style={{ height: '80px', width: '80px' }} />
            )}
            <button onClick={() => setMobileOpen(false)} className="action-btn">
              <X size={22} />
            </button>
          </div>
          <nav className="mobile-nav">
            {/* Home, About first */}
            {beforeLinks.map(link => (
              <Link
                key={link.label}
                to={link.href}
                className="mobile-nav-link"
                onClick={() => setMobileOpen(false)}
              >
                {link.label}
              </Link>
            ))}

            {/* Categories accordion */}
            <div>
              <button
                className="mobile-nav-link mobile-shop-toggle"
                onClick={() => setMobileShopOpen(!mobileShopOpen)}
              >
                Categories
                <ChevronDown size={18} style={{ transform: mobileShopOpen ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
              </button>
              {mobileShopOpen && (
                <div className="mobile-categories">
                  {categories.map(cat => (
                    <Link
                      key={cat.id}
                      to={`/shop?cat=${cat.slug}`}
                      className="mobile-cat-link"
                      onClick={() => setMobileOpen(false)}
                    >
                      <span>{cat.emoji}</span>
                      <span>{cat.name}</span>
                      <span className="mobile-cat-count">{cat.count}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Combos after */}
            {afterLinks.map(link => (
              <Link
                key={link.label}
                to={link.href}
                className="mobile-nav-link"
                onClick={() => setMobileOpen(false)}
              >
                {link.label}
              </Link>
            ))}
          </nav>
          <div className="mobile-bottom">
            <Link to="/cart" className="btn-primary" onClick={() => setMobileOpen(false)} style={{ justifyContent: 'center' }}>
              <Truck size={16} /> View Cart {cartCount > 0 && `(${cartCount})`}
            </Link>
          </div>
        </div>
      )}
      <LoginModal isOpen={isLoginModalOpen} onClose={() => setLoginModalOpen(false)} />
    </>
  );
}
